<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div v-if="!canManageAppointments" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="alert alert-warning mb-0">
              You do not have permission to manage appointment settings for this business.
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="row">
        <div class="col-xl-4">
          <div class="card h-100 settings-card">
            <div class="card-body settings-card-body">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h4 class="card-title mb-1">Appointment Settings</h4>
                  <p class="text-muted mb-0">Configure how this business handles appointment booking.</p>
                </div>
                <span class="badge settings-status-badge" :class="settings.enabled ? 'bg-soft-success text-success' : 'bg-soft-secondary text-secondary'">
                  {{ settings.enabled ? "Enabled" : "Disabled" }}
                </span>
              </div>

              <div class="settings-hero mb-4">
                <div>
                  <div class="settings-hero-label">Module Status</div>
                  <h5 class="mb-1">{{ settings.enabled ? "Booking is live" : "Booking is paused" }}</h5>
                  <p class="text-muted mb-0">Changes made here affect how patients discover, book, cancel, and pay for appointments.</p>
                </div>
                <div class="settings-hero-switch">
                    <button
                      type="button"
                      class="live-toggle"
                      :class="{ active: settings.enabled, loading: activeSettingToggleKey === 'enabled' }"
                      :aria-pressed="settings.enabled ? 'true' : 'false'"
                      @click="handleSettingsToggle('enabled', !settings.enabled)"
                    >
                      <span class="live-toggle-track">
                        <span class="live-toggle-state">{{ settings.enabled ? "On" : "Off" }}</span>
                        <span class="live-toggle-thumb"></span>
                      </span>
                    </button>
                  <small v-if="activeSettingToggleKey === 'enabled'" class="settings-saving-copy">Saving change...</small>
                </div>
              </div>

              <form @submit.prevent="saveSettings">
                <div class="mb-3 select-field">
                  <label class="form-label">Approval Mode</label>
                  <multiselect
                    :value="selectOption(approvalModeOptions, settings.approval_mode)"
                    :options="approvalModeOptions"
                    track-by="value"
                    label="text"
                    :show-labels="false"
                    :searchable="false"
                    :allow-empty="false"
                    placeholder="Choose approval mode"
                    class="appointment-select"
                    :disabled="settingsFormLocked"
                    @input="settings.approval_mode = $event.value"
                  />
                </div>

                <div class="mb-3 select-field">
                  <label class="form-label">Default Payment Mode</label>
                  <multiselect
                    :value="selectOption(paymentModeOptions, settings.default_payment_mode)"
                    :options="paymentModeOptions"
                    track-by="value"
                    label="text"
                    :show-labels="false"
                    :searchable="false"
                    :allow-empty="false"
                    placeholder="Choose payment mode"
                    class="appointment-select"
                    :disabled="settingsFormLocked"
                    @input="settings.default_payment_mode = $event.value"
                  />
                </div>

                <div class="settings-inline-switch mb-3">
                  <div>
                    <label class="form-label d-block mb-1">Allow Patient Cancellation</label>
                    <small class="text-muted">Decide whether patients can cancel using the app.</small>
                  </div>
                  <div class="settings-inline-switch-control">
                    <button
                      type="button"
                      class="live-toggle"
                      :class="{ active: settings.cancellation_allowed, loading: activeSettingToggleKey === 'cancellation_allowed' }"
                      :aria-pressed="settings.cancellation_allowed ? 'true' : 'false'"
                      :disabled="settingsFormLocked"
                      @click="handleSettingsToggle('cancellation_allowed', !settings.cancellation_allowed)"
                    >
                      <span class="live-toggle-track">
                        <span class="live-toggle-state">{{ settings.cancellation_allowed ? "On" : "Off" }}</span>
                        <span class="live-toggle-thumb"></span>
                      </span>
                    </button>
                    <small v-if="activeSettingToggleKey === 'cancellation_allowed'" class="settings-saving-copy">Saving change...</small>
                  </div>
                </div>

                <div class="row">
                  <div v-if="showCancellationCutoff" class="col-md-6 mb-3">
                    <label class="form-label">Cancellation Cutoff (Hours)</label>
                    <b-form-input v-model="settings.cancellation_cutoff_hours" type="number" min="0" :disabled="settingsFormLocked"></b-form-input>
                  </div>
                  <div v-if="showRefundCutoff" class="col-md-6 mb-3">
                    <label class="form-label">Refund Cutoff (Hours)</label>
                    <b-form-input v-model="settings.refund_cutoff_hours" type="number" min="0" :disabled="settingsFormLocked"></b-form-input>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Max Breaks Per Availability</label>
                    <b-form-input v-model="settings.max_breaks_per_availability" type="number" min="0" max="10" :disabled="settingsFormLocked"></b-form-input>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Break Duration (Minutes)</label>
                    <b-form-input v-model="settings.default_break_duration_minutes" type="number" min="5" :disabled="settingsFormLocked"></b-form-input>
                  </div>
                </div>

                <div class="d-flex justify-content-end">
                  <b-button type="submit" variant="primary" :disabled="savingSettings || settingsFormLocked">
                    <b-spinner v-if="savingSettings" small class="align-middle me-1"></b-spinner>
                    {{ savingSettings ? "Saving..." : "Save Settings" }}
                  </b-button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="card h-100 services-card">
            <div class="card-body services-card-body">
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h4 class="card-title mb-1">Bookable Services</h4>
                  <p class="text-muted mb-0">Decide which services can be booked from the app, and define their slot duration and price.</p>
                </div>
                <div class="appointment-search">
                  <b-form-input v-model="serviceFilter" type="search" placeholder="Search services..."></b-form-input>
                </div>
              </div>

              <div class="services-summary mb-3">
                <div class="summary-pill summary-pill-success">
                  <span class="summary-count">{{ enabledServicesCount }}</span>
                  <span class="summary-label">Bookable Services</span>
                </div>
                <div class="summary-pill">
                  <span class="summary-count">{{ disabledServicesCount }}</span>
                  <span class="summary-label">Unbookable</span>
                </div>
              </div>

              <div class="table-responsive">
                <b-table
                  :items="pagedServices"
                  :fields="serviceFields"
                  :busy="loadingServices"
                  responsive="md"
                  show-empty
                  empty-text="No services found."
                  :per-page="servicePerPage"
                  :current-page="serviceCurrentPage"
                  tbody-tr-class="service-table-row"
                  table-class="service-table"
                >
                  <template #table-busy>
                    <div class="text-center text-primary my-3">
                      <b-spinner class="align-middle me-1" small></b-spinner>
                      <strong>Loading services...</strong>
                    </div>
                  </template>

                  <template #cell(service_name)="data">
                    <div class="service-name-cell">
                      <div class="service-priority-dot" :class="{ active: data.item.enabled }"></div>
                      <div>
                      <strong>{{ data.item.service_name || "Unnamed Service" }}</strong>
                      <div class="small text-muted">{{ data.item.service_category_name || "No category" }}</div>
                      </div>
                    </div>
                  </template>

                  <template #cell(enabled)="data">
                    <span class="badge" :class="data.item.enabled ? 'bg-soft-success text-success' : 'bg-soft-secondary text-secondary'">
                      {{ data.item.enabled ? "Bookable" : "Disabled" }}
                    </span>
                  </template>

                  <template #cell(default_slot_duration_minutes)="data">
                    {{ data.item.default_slot_duration_minutes || 0 }} mins
                  </template>

                  <template #cell(default_price)="data">
                    {{ money(data.item.default_price || data.item.service_default_price || 0) }}
                  </template>

                  <template #cell(actions)="data">
                    <b-button size="sm" variant="primary" class="service-action-button" :disabled="settingsFormLocked" @click="openServiceModal(data.item)">
                      <i class="mdi mdi-cog-outline me-1"></i>
                      Configure
                    </b-button>
                  </template>
                </b-table>
              </div>

              <div class="row mt-3">
                <div class="col-sm-12 col-md-6">
                  <div class="dataTables_length">
                    <label class="d-inline-flex align-items-center mb-0">
                      Show
                      <b-form-select class="form-select form-select-sm mx-2 table-page-select" v-model="servicePerPage" size="sm" :options="servicePageOptions"></b-form-select>
                      entries
                    </label>
                  </div>
                </div>
                <div class="col-sm-12 col-md-6">
                  <div class="dataTables_paginate paging_simple_numbers float-md-end mt-2 mt-md-0">
                    <ul class="pagination pagination-rounded mb-0">
                      <b-pagination v-model="serviceCurrentPage" :total-rows="filteredServices.length" :per-page="servicePerPage"></b-pagination>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-modal id="appointment-service-modal" centered size="lg" hide-footer title="Configure Bookable Service" modal-class="appointment-service-modal">
      <form @submit.prevent="saveServiceConfig">
        <div class="service-modal-hero mb-4">
          <div>
            <div class="service-modal-eyebrow">Bookable Service</div>
            <h4 class="mb-1">{{ selectedService.service_name || "Service" }}</h4>
            <p class="text-muted mb-0">{{ selectedService.service_category_name || "No category assigned" }}</p>
          </div>
          <div class="service-modal-toggle">
            <span class="badge" :class="serviceForm.enabled ? 'bg-soft-success text-success' : 'bg-soft-secondary text-secondary'">
              {{ serviceForm.enabled ? "Live In App" : "Not Bookable" }}
            </span>
            <button
              type="button"
              class="live-toggle"
              :class="{ active: serviceForm.enabled, loading: savingServiceToggle }"
              :aria-pressed="serviceForm.enabled ? 'true' : 'false'"
              :disabled="settingsFormLocked"
              @click="handleServiceToggle(!serviceForm.enabled)"
            >
              <span class="live-toggle-track">
                <span class="live-toggle-state">{{ serviceForm.enabled ? "On" : "Off" }}</span>
                <span class="live-toggle-thumb"></span>
              </span>
            </button>
            <small v-if="savingServiceToggle" class="settings-saving-copy">Saving change...</small>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Slot Duration (Minutes)</label>
            <b-form-input v-model="serviceForm.default_slot_duration_minutes" type="number" min="5" step="5" :disabled="settingsFormLocked"></b-form-input>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Appointment Price</label>
            <b-form-input v-model="serviceForm.default_price" type="number" min="0" step="0.01" :disabled="settingsFormLocked"></b-form-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3 select-field">
            <label class="form-label">Payment Mode</label>
            <multiselect
              :value="selectOption(overridePaymentOptions, serviceForm.payment_mode_override)"
              :options="overridePaymentOptions"
              track-by="value"
              label="text"
              :show-labels="false"
              :searchable="false"
              :allow-empty="true"
              placeholder="Choose payment mode"
              class="appointment-select"
              :disabled="settingsFormLocked"
              @input="serviceForm.payment_mode_override = $event ? $event.value : null"
            />
          </div>
          <div class="col-md-6 mb-3 select-field">
            <label class="form-label">Approval Mode</label>
            <multiselect
              :value="selectOption(overrideApprovalOptions, serviceForm.approval_mode_override)"
              :options="overrideApprovalOptions"
              track-by="value"
              label="text"
              :show-labels="false"
              :searchable="false"
              :allow-empty="true"
              placeholder="Choose approval mode"
              class="appointment-select"
              :disabled="settingsFormLocked"
              @input="serviceForm.approval_mode_override = $event ? $event.value : null"
            />
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label">Notes</label>
          <b-form-textarea v-model="serviceForm.notes" rows="3" placeholder="Optional internal notes for this bookable service" :disabled="settingsFormLocked"></b-form-textarea>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('appointment-service-modal')">Close</b-button>
          <b-button type="submit" variant="primary" :disabled="savingService || settingsFormLocked">
            <b-spinner v-if="savingService" small class="align-middle me-1"></b-spinner>
            {{ savingService ? "Saving..." : "Save Service" }}
          </b-button>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Appointment Settings",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    Multiselect,
  },
  data() {
    return {
      title: "Appointment Settings",
      items: [
        { text: "Dashboard", to: "/" },
        { text: "Appointment Settings", active: true },
      ],
      userData: JSON.parse(localStorage.getItem("user")) || {},
      loadingServices: false,
      savingSettings: false,
      savingService: false,
      activeSettingToggleKey: "",
      pendingSettingToggleKeys: [],
      savingServiceToggle: false,
      serviceToggleLocked: false,
      serviceFilter: "",
      serviceCurrentPage: 1,
      servicePerPage: 10,
      servicePageOptions: [10, 20, 50, 100],
      services: [],
      selectedService: {},
      settings: {
        enabled: false,
        approval_mode: "manual",
        default_payment_mode: "pay_on_arrival",
        cancellation_allowed: true,
        cancellation_cutoff_hours: 24,
        refund_cutoff_hours: 24,
        max_breaks_per_availability: 2,
        default_break_duration_minutes: 15,
      },
      serviceForm: {
        enabled: false,
        default_slot_duration_minutes: 30,
        default_price: 0,
        payment_mode_override: null,
        approval_mode_override: null,
        notes: "",
      },
      serviceFields: [
        { key: "service_name", label: "Service" },
        { key: "enabled", label: "Status" },
        { key: "default_slot_duration_minutes", label: "Slot Duration" },
        { key: "default_price", label: "Price" },
        { key: "actions", label: "Actions" },
      ],
      approvalModeOptions: [
        { value: "manual", text: "Manual Approval" },
        { value: "auto", text: "Auto Approval" },
      ],
      paymentModeOptions: [
        { value: "pay_on_arrival", text: "Pay On Arrival" },
        { value: "pay_in_app", text: "Pay In App" },
      ],
      overridePaymentOptions: [
        { value: null, text: "Use Business Default" },
        { value: "pay_on_arrival", text: "Pay On Arrival" },
        { value: "pay_in_app", text: "Pay In App" },
      ],
      overrideApprovalOptions: [
        { value: null, text: "Use Business Default" },
        { value: "manual", text: "Manual Approval" },
        { value: "auto", text: "Auto Approval" },
      ],
    };
  },
  computed: {
    canManageAppointments() {
      return Number(this.userData?.permissions?.appointment_manager || 0) === 1;
    },
    settingsFormLocked() {
      return !this.settings.enabled;
    },
    showRefundCutoff() {
      return this.settings.default_payment_mode !== "pay_on_arrival";
    },
    showCancellationCutoff() {
      return this.settings.cancellation_allowed;
    },
    filteredServices() {
      const search = (this.serviceFilter || "").trim().toLowerCase();
      const filtered = !search
        ? this.services.slice()
        : this.services.filter((item) =>
        [item.service_name, item.service_category_name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search))
      );

      return filtered.sort((left, right) => {
        const enabledDiff = Number(right.enabled || 0) - Number(left.enabled || 0);
        if (enabledDiff !== 0) return enabledDiff;
        return String(left.service_name || "").localeCompare(String(right.service_name || ""));
      });
    },
    pagedServices() {
      const start = (this.serviceCurrentPage - 1) * this.servicePerPage;
      return this.filteredServices.slice(start, start + this.servicePerPage);
    },
    enabledServicesCount() {
      return this.services.filter((item) => Number(item.enabled || 0) === 1).length;
    },
    disabledServicesCount() {
      return this.services.filter((item) => Number(item.enabled || 0) !== 1).length;
    },
  },
  mounted() {
    if (this.canManageAppointments) {
      this.initialLoad();
    }
  },
  methods: {
    selectOption(options, value) {
      return (options || []).find((item) => item.value === value) || null;
    },
    async initialLoad() {
      await Promise.all([this.fetchSettings(), this.fetchServices()]);
    },
    async fetchSettings() {
      const response = await axios.get("/appointmentsettings");
      const settings = response.data?.settings || {};
      this.settings = {
        enabled: Number(settings.enabled || 0) === 1,
        approval_mode: settings.approval_mode || "manual",
        default_payment_mode: settings.default_payment_mode || "pay_on_arrival",
        cancellation_allowed: Number(settings.cancellation_allowed || 0) === 1,
        cancellation_cutoff_hours: settings.cancellation_cutoff_hours ?? 24,
        refund_cutoff_hours: settings.refund_cutoff_hours ?? 24,
        max_breaks_per_availability: settings.max_breaks_per_availability ?? 2,
        default_break_duration_minutes: settings.default_break_duration_minutes ?? 15,
      };
    },
    async fetchServices() {
      this.loadingServices = true;
      try {
        const response = await axios.get("/appointmentservices");
        this.services = response.data?.appointment_services || [];
      } finally {
        this.loadingServices = false;
      }
    },
    buildSettingsFormData() {
      const formData = new FormData();
      formData.append("enabled", this.settings.enabled ? 1 : 0);
      formData.append("approval_mode", this.settings.approval_mode);
      formData.append("default_payment_mode", this.settings.default_payment_mode);
      formData.append("cancellation_allowed", this.settings.cancellation_allowed ? 1 : 0);
      formData.append("cancellation_cutoff_hours", this.settings.cancellation_cutoff_hours);
      formData.append("refund_cutoff_hours", this.settings.refund_cutoff_hours);
      formData.append("max_breaks_per_availability", this.settings.max_breaks_per_availability);
      formData.append("default_break_duration_minutes", this.settings.default_break_duration_minutes);
      return formData;
    },
    async handleSettingsToggle(key, value) {
      if (this.settingsFormLocked && key !== "enabled") return;
      if (this.pendingSettingToggleKeys.includes(key)) return;
      const previous = this.settings[key];
      if (previous === value) return;
      this.settings[key] = value;
      this.activeSettingToggleKey = key;
      this.pendingSettingToggleKeys = [...this.pendingSettingToggleKeys, key];
      try {
        const response = await axios.post("/updateappointmentsettings", this.buildSettingsFormData());
        this.$toast.success(response.data?.message || "Appointment settings updated successfully.");
        await this.fetchSettings();
      } catch (error) {
        this.settings[key] = previous;
        this.showRequestError(error, "Failed to update appointment setting.");
      } finally {
        this.pendingSettingToggleKeys = this.pendingSettingToggleKeys.filter((item) => item !== key);
        this.activeSettingToggleKey = "";
      }
    },
    async saveSettings() {
      if (this.settingsFormLocked) return;
      this.savingSettings = true;
      try {
        const response = await axios.post("/updateappointmentsettings", this.buildSettingsFormData());
        this.$toast.success(response.data?.message || "Appointment settings updated successfully.");
        await this.fetchSettings();
      } catch (error) {
        this.showRequestError(error, "Failed to save appointment settings.");
      } finally {
        this.savingSettings = false;
      }
    },
    openServiceModal(service) {
      if (this.settingsFormLocked) return;
      this.selectedService = { ...service };
      this.serviceForm = {
        enabled: Number(service.enabled || 0) === 1,
        default_slot_duration_minutes: service.default_slot_duration_minutes || 30,
        default_price: service.default_price ?? service.service_default_price ?? 0,
        payment_mode_override: service.payment_mode_override || null,
        approval_mode_override: service.approval_mode_override || null,
        notes: service.notes || "",
      };
      this.$bvModal.show("appointment-service-modal");
    },
    buildServiceFormData() {
      const formData = new FormData();
      formData.append("service_id", this.selectedService.service_id);
      formData.append("enabled", this.serviceForm.enabled ? 1 : 0);
      formData.append("default_slot_duration_minutes", this.serviceForm.default_slot_duration_minutes);
      formData.append("default_price", this.serviceForm.default_price);
      if (this.serviceForm.payment_mode_override) {
        formData.append("payment_mode_override", this.serviceForm.payment_mode_override);
      }
      if (this.serviceForm.approval_mode_override) {
        formData.append("approval_mode_override", this.serviceForm.approval_mode_override);
      }
      formData.append("notes", this.serviceForm.notes || "");
      return formData;
    },
    async handleServiceToggle(value) {
      if (this.settingsFormLocked) return;
      if (this.serviceToggleLocked) return;
      const previous = this.serviceForm.enabled;
      if (previous === value) return;
      this.serviceForm.enabled = value;
      this.serviceToggleLocked = true;
      this.savingServiceToggle = true;
      try {
        const response = await axios.post("/saveappointmentservice", this.buildServiceFormData());
        this.$toast.success(response.data?.message || "Bookable service updated successfully.");
        this.selectedService = {
          ...this.selectedService,
          enabled: value ? 1 : 0,
        };
        await this.fetchServices();
      } catch (error) {
        this.serviceForm.enabled = previous;
        this.showRequestError(error, "Failed to update bookable service.");
      } finally {
        this.serviceToggleLocked = false;
        this.savingServiceToggle = false;
      }
    },
    async saveServiceConfig() {
      if (this.settingsFormLocked) return;
      this.savingService = true;
      try {
        const response = await axios.post("/saveappointmentservice", this.buildServiceFormData());

        this.$toast.success(response.data?.message || "Bookable service updated successfully.");
        this.$bvModal.hide("appointment-service-modal");
        await this.fetchServices();
      } catch (error) {
        this.showRequestError(error, "Failed to save bookable service.");
      } finally {
        this.savingService = false;
      }
    },
    showRequestError(error, fallbackMessage) {
      const message =
        error?.response?.data?.message ||
        Object.values(error?.response?.data?.errors || {})?.[0]?.[0] ||
        fallbackMessage;
      this.$toast.error(message);
    },
    money(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.appointment-search {
  min-width: 280px;
}

.settings-card,
.services-card {
  border: 1px solid #dde7f1;
  border-radius: 1.2rem;
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(16, 185, 129, 0.08), transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #f9fcfb 100%);
}

.settings-card-body,
.services-card-body {
  padding: 1.4rem;
}

.settings-status-badge {
  font-size: 0.78rem;
  padding: 0.45rem 0.7rem;
}

.settings-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border: 1px solid #dbe7df;
  border-radius: 1rem;
  background: linear-gradient(135deg, rgba(236, 253, 245, 0.95), rgba(248, 250, 252, 0.9));
}

.settings-hero-label,
.service-modal-eyebrow {
  margin-bottom: 0.35rem;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0f766e;
}

.settings-hero-switch,
.service-modal-toggle,
.settings-inline-switch-control {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
}

.settings-saving-copy {
  font-size: 0.75rem;
  color: #0f766e;
}

.live-toggle {
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.live-toggle-track {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  box-shadow: inset 0 2px 5px rgba(15, 23, 42, 0.08);
  transition: background 0.22s ease, box-shadow 0.22s ease;
}

.live-toggle.active .live-toggle-track {
  background: linear-gradient(135deg, #10b981, #0f766e);
  box-shadow: inset 0 2px 7px rgba(6, 95, 70, 0.18);
}

.live-toggle.loading {
  opacity: 0.78;
  pointer-events: none;
}

.live-toggle-state {
  position: relative;
  z-index: 1;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #475569;
  padding-inline: 8px 0;
}

.live-toggle.active .live-toggle-state {
  color: #ffffff;
  padding-inline: 0 8px;
}

.live-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.16);
  transition: transform 0.22s ease;
}

.live-toggle.active .live-toggle-thumb {
  transform: translateX(32px);
}

.settings-inline-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.95rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.84);
}

.select-field {
  position: relative;
}

.select-field::after {
  content: "";
  position: absolute;
  right: 1rem;
  top: calc(50% + 0.7rem);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid #0f766e;
  pointer-events: none;
  transition: transform 0.18s ease;
}

.select-field:focus-within::after {
  transform: rotate(180deg);
}

.services-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.summary-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.95rem;
  border: 1px solid #dbe4f0;
  border-radius: 999px;
  background: #fff;
}

.summary-pill-success {
  border-color: rgba(16, 185, 129, 0.2);
  background: rgba(236, 253, 245, 0.9);
}

.summary-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: #0f766e;
  color: #fff;
  font-weight: 700;
}

.summary-label {
  font-weight: 600;
  color: #334155;
}

.service-table-row {
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.service-table-row:hover {
  transform: translateY(-1px);
  box-shadow: inset 0 0 0 999px rgba(240, 253, 250, 0.72);
}

.service-name-cell {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
}

.service-priority-dot {
  width: 0.75rem;
  height: 0.75rem;
  margin-top: 0.35rem;
  border-radius: 999px;
  background: #cbd5e1;
  box-shadow: 0 0 0 6px rgba(203, 213, 225, 0.16);
}

.service-priority-dot.active {
  background: #10b981;
  box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.16);
}

.service-action-button {
  border-radius: 999px;
  padding-inline: 0.85rem;
}

.table-page-select {
  max-width: 88px;
}

.service-modal-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.05rem 1.1rem;
  border: 1px solid #dbe7df;
  border-radius: 1.05rem;
  background: linear-gradient(135deg, rgba(240, 253, 250, 0.95), rgba(248, 250, 252, 0.92));
}

:deep(.appointment-select .multiselect__tags) {
  min-height: 46px;
  padding: 0.75rem 2.5rem 0.72rem 0.9rem;
  border: 1px solid #d7e2ee;
  border-radius: 0.95rem;
  background: #fff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

:deep(.appointment-select .multiselect__single),
:deep(.appointment-select .multiselect__input),
:deep(.appointment-select .multiselect__placeholder) {
  margin-bottom: 0;
  padding-top: 0;
  font-size: 0.96rem;
  color: #0f172a;
}

:deep(.appointment-select .multiselect__select) {
  height: 44px;
  width: 42px;
  opacity: 0;
}

:deep(.appointment-select.multiselect--active .multiselect__tags) {
  border-color: rgba(15, 118, 110, 0.42);
  box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.1);
}

:deep(.appointment-select .multiselect__content-wrapper) {
  margin-top: 0.4rem;
  border: 1px solid #dbe4f0;
  border-radius: 1rem;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

:deep(.appointment-select .multiselect__option--highlight) {
  background: rgba(15, 118, 110, 0.1);
  color: #0f172a;
}

:deep(.appointment-select .multiselect__option--selected) {
  background: rgba(16, 185, 129, 0.14);
  color: #065f46;
  font-weight: 600;
}

:deep(.appointment-service-modal .modal-content) {
  border-radius: 1.2rem;
  overflow: hidden;
}

:deep(.appointment-service-modal .modal-header) {
  border-bottom: 1px solid #eef2f7;
}

@media (max-width: 991.98px) {
  .settings-hero,
  .service-modal-hero,
  .settings-inline-switch {
    flex-direction: column;
    align-items: flex-start;
  }

  .settings-hero-switch,
  .service-modal-toggle,
  .settings-inline-switch-control {
    align-items: flex-start;
  }
}
</style>
